import React, { useState, useEffect } from 'react';
import { InitiativesDB } from '../../utils/initiativesData';
import { Button } from '../ui';

const CATEGORIES = ['access', 'quality', 'staffing', 'finance', 'other'];

function FlatIconTrash({ size = 24, title = 'Remove', onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        background: '#EF4444',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.5,
        fontWeight: 600,
        flexShrink: 0,
        padding: 0
      }}
    >
      ✕
    </button>
  );
}
const STATUSES = ['planning', 'in-progress', 'on-hold', 'completed'];
const OPERATORS = ['<', '<=', '>', '>=', '=='];

export default function InitiativeEditModal({ initiativeId, onClose, onSaved }) {
  const [initiative, setInitiative] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'access',
    status: 'planning',
    programManagerName: '',
    percentComplete: 0,
    startDate: '',
    dueDate: ''
  });
  const [kpis, setKpis] = useState([]);
  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [initiativeId, onClose]);

  useEffect(() => {
    if (!initiativeId) return;
    const init = InitiativesDB.getInitiative(initiativeId);
    if (!init) return;
    setInitiative(init);
    setForm({
      name: init.name || '',
      description: init.description || '',
      category: init.category || 'access',
      status: init.status || 'planning',
      programManagerName: init.programManagerName || '',
      percentComplete: init.percentComplete ?? 0,
      startDate: init.startDate ? init.startDate.split('T')[0] : '',
      dueDate: init.dueDate ? init.dueDate.split('T')[0] : ''
    });
    setKpis((init.kpis || []).map(k => ({
      id: k.id,
      kpiName: k.kpiName || '',
      currentValue: k.currentValue,
      targetOperator: k.targetOperator || '<',
      targetValue: k.targetValue,
      unit: k.unit || ''
    })));
    setMilestones((init.milestones || []).map(m => ({
      id: m.id,
      name: m.name || '',
      dueDate: m.dueDate ? m.dueDate.split('T')[0] : '',
      completed: !!m.completed
    })));
  }, [initiativeId]);

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addKPI = () => {
    setKpis(prev => [...prev, { id: null, kpiName: '', currentValue: '', targetOperator: '<', targetValue: '', unit: '' }]);
  };

  const updateKPI = (index, field, value) => {
    setKpis(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeKPI = (index) => {
    setKpis(prev => prev.filter((_, i) => i !== index));
  };

  const addMilestone = () => {
    setMilestones(prev => [...prev, { id: null, name: '', dueDate: '', completed: false }]);
  };

  const updateMilestone = (index, field, value) => {
    setMilestones(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeMilestone = (index) => {
    setMilestones(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!initiativeId || !initiative) return;

    const base = InitiativesDB.getInitiative(initiativeId);
    const updated = {
      ...base,
      ...form,
      percentComplete: Number(form.percentComplete) || 0
    };
    InitiativesDB.saveInitiative(updated);

    InitiativesDB.deleteKPIsForInitiative(initiativeId);
    kpis.forEach((k) => {
      if (k.kpiName && String(k.kpiName).trim()) {
        InitiativesDB.saveKPI({
          id: k.id || InitiativesDB.generateId('KPI'),
          initiativeId: initiativeId,
          kpiName: k.kpiName.trim(),
          currentValue: k.currentValue === '' || k.currentValue == null ? null : Number(k.currentValue),
          targetOperator: k.targetOperator || '<',
          targetValue: k.targetValue === '' || k.targetValue == null ? null : Number(k.targetValue),
          unit: (k.unit || '').trim()
        });
      }
    });

    InitiativesDB.deleteMilestonesForInitiative(initiativeId);
    milestones.forEach((m, idx) => {
      if (m.name && m.dueDate) {
        InitiativesDB.saveMilestone({
          id: m.id || InitiativesDB.generateId('MILE'),
          initiativeId: initiativeId,
          name: m.name,
          dueDate: m.dueDate,
          completed: !!m.completed
        });
      }
    });

    InitiativesDB.updateInitiativeHealth(initiativeId);
    onSaved?.();
    onClose?.();
    if (typeof window !== 'undefined') {
      const msg = document.createElement('div');
      msg.textContent = 'Changes saved. List updated.';
      msg.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#10B981;color:white;padding:12px 20px;border-radius:8px;font-size:14px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
      document.body.appendChild(msg);
      setTimeout(() => msg.remove(), 2500);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!initiative) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      style={{ overflowY: 'auto' }}
      onClick={handleOverlayClick}
    >
      <div
        className="bg-nn-white rounded-card-lg shadow-md w-full"
        style={{
          maxWidth: 900,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between"
          style={{
            background: 'linear-gradient(135deg, #00CED1 0%, #2C5FA8 100%)',
            color: 'white',
            padding: '20px 30px',
            borderRadius: '12px 12px 0 0',
          }}
        >
          <h2 style={{ fontWeight: 300, fontSize: 24, margin: 0 }}>View / Edit Initiative</h2>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }} aria-label="Close">✕</Button>
        </div>

        <div style={{ padding: 30 }}>
          <div style={{ marginBottom: 30 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 15, color: '#374151' }}>Basic Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 15 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Initiative Name</label>
                <input type="text" value={form.name} onChange={e => handleFormChange('name', e.target.value)} required className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Description</label>
                <textarea value={form.description} onChange={e => handleFormChange('description', e.target.value)} rows={3} className="w-full rounded-lg border border-nn-border bg-nn-white px-3 py-2 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ resize: 'vertical' }} />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Category</label>
                <select value={form.category} onChange={e => handleFormChange('category', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Status</label>
                <select value={form.status} onChange={e => handleFormChange('status', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Program Manager</label>
                <input type="text" value={form.programManagerName} onChange={e => handleFormChange('programManagerName', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">% Complete</label>
                <input type="number" min={0} max={100} value={form.percentComplete} onChange={e => handleFormChange('percentComplete', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Start Date</label>
                <input type="date" value={form.startDate} onChange={e => handleFormChange('startDate', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Due Date</label>
                <input type="date" value={form.dueDate} onChange={e => handleFormChange('dueDate', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 30 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 15, color: '#374151' }}>Key Performance Indicators</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {kpis.map((kpi, idx) => (
                <div key={idx} style={{ background: '#F9FAFB', padding: 15, borderRadius: 6, border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>KPI Name</label>
                      <input type="text" value={kpi.kpiName} onChange={e => updateKPI(idx, 'kpiName', e.target.value)} placeholder="e.g., Average Wait Time" className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Current</label>
                      <input type="number" step="0.1" value={kpi.currentValue} onChange={e => updateKPI(idx, 'currentValue', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Op</label>
                      <select value={kpi.targetOperator} onChange={e => updateKPI(idx, 'targetOperator', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
                        {OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Target</label>
                      <input type="number" step="0.1" value={kpi.targetValue} onChange={e => updateKPI(idx, 'targetValue', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Unit</label>
                      <input type="text" value={kpi.unit} onChange={e => updateKPI(idx, 'unit', e.target.value)} placeholder="min" className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
                    </div>
                    <FlatIconTrash size={28} title="Remove KPI" onClick={() => removeKPI(idx)} />
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="primary" size="sm" onClick={addKPI} className="mt-2.5 w-full" style={{ border: '2px dashed #00CED1' }}>+ Add KPI</Button>
          </div>

          <div style={{ marginBottom: 30 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 15, color: '#374151' }}>Milestones</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {milestones.map((m, idx) => (
                <div key={idx} style={{ background: '#F9FAFB', padding: '12px 15px', borderRadius: 6, border: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <input type="checkbox" checked={m.completed} onChange={e => updateMilestone(idx, 'completed', e.target.checked)} />
                    <input type="text" value={m.name} onChange={e => updateMilestone(idx, 'name', e.target.value)} placeholder="Milestone name" className="h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ flex: 1 }} />
                    <input type="date" value={m.dueDate} onChange={e => updateMilestone(idx, 'dueDate', e.target.value)} className="h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
                  </div>
                  <FlatIconTrash size={28} title="Remove milestone" onClick={() => removeMilestone(idx)} />
                </div>
              ))}
            </div>
            <Button type="button" variant="primary" size="sm" onClick={addMilestone} className="mt-2.5 w-full" style={{ border: '2px dashed #00CED1' }}>+ Add Milestone</Button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-nn-border" style={{ padding: '20px 30px' }}>
          <Button type="button" variant="default" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="button" variant="primary" size="sm" onClick={handleSave}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
}
